import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { QRCodeCanvas } from 'qrcode.react';
import { Star, Award, Loader2, Gift } from 'lucide-react';

/**
 * Pagina pubblica della tessera fedeltà (accessibile scansionando il QR,
 * senza login). Mostra logo attività, nome cliente, punti reali e storico.
 */
export default function PublicLoyaltyCard() {
  const { t } = useTranslation();
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/loyalty/${code}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? t('publicLoyalty.notFound') : t('publicLoyalty.loadError'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <Gift className="h-12 w-12 text-slate-300 mb-3" />
        <div className="text-lg font-semibold text-slate-700">{error}</div>
        <div className="text-sm text-slate-500 mt-1">{t('publicLoyalty.code')}: {code}</div>
      </div>
    );
  }

  const b = data.business || {};
  const m = data.member || {};
  const rewards = data.rewards || [];
  const nextReward = [...rewards].sort((a, b) => a.points_cost - b.points_cost).find((r) => r.points_cost > (m.points || 0));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 py-8 px-4 flex justify-center">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          {/* Header con logo */}
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 px-6 py-7 text-center text-white">
            {b.logo ? (
              <img src={b.logo} alt={b.name} className="mx-auto max-h-20 max-w-[70%] object-contain mb-2 bg-white/90 rounded-xl p-2" />
            ) : (
              <div className="text-2xl font-extrabold tracking-tight">{b.name}</div>
            )}
            {b.logo && b.name ? <div className="text-sm font-semibold opacity-95">{b.name}</div> : null}
            <div className="mt-1 text-xs uppercase tracking-widest opacity-90">{t('publicLoyalty.loyaltyCard')}</div>
          </div>

          {/* Corpo */}
          <div className="px-6 py-6">
            <div className="text-center">
              <div className="text-sm text-slate-500">{t('publicLoyalty.hello')}</div>
              <div className="text-xl font-bold text-slate-800">{m.name}</div>
            </div>

            <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-100 py-5 text-center">
              <div className="flex items-center justify-center gap-2 text-amber-600">
                <Star className="h-5 w-5 fill-amber-500" />
                <span className="text-5xl font-extrabold leading-none text-slate-800">{m.points}</span>
              </div>
              <div className="text-sm font-medium text-slate-500 mt-1">{t('publicLoyalty.points')}</div>
            </div>

            {nextReward ? (
              <div className="mt-4 text-center text-sm text-slate-600">
                {t('publicLoyalty.missing1')} <span className="font-bold text-amber-600">{nextReward.points_cost - m.points}</span> {t('publicLoyalty.missing2')} <span className="font-semibold">{nextReward.name}</span>
              </div>
            ) : null}

            {/* Premi disponibili */}
            {rewards.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('publicLoyalty.rewards')}</div>
                <div className="space-y-2">
                  {rewards.map((r) => {
                    const ok = (m.points || 0) >= r.points_cost;
                    return (
                      <div key={r.id} className={`flex items-center justify-between rounded-xl border p-3 ${ok ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{r.name}</div>
                          {r.description ? <div className="text-xs text-slate-500 truncate">{r.description}</div> : null}
                        </div>
                        <div className={`text-sm font-bold shrink-0 ml-2 ${ok ? 'text-amber-600' : 'text-slate-400'}`}>
                          <Award className="h-4 w-4 inline mr-1" />{r.points_cost}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Storico */}
            {m.history && m.history.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('publicLoyalty.lastMovements')}</div>
                <div className="space-y-1.5">
                  {m.history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                      <span className="text-slate-500 truncate mr-2">{h.note || h.type}</span>
                      <span className={`font-semibold shrink-0 ${h.points >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>{h.points >= 0 ? '+' : ''}{h.points}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* QR + codice */}
            <div className="mt-6 flex flex-col items-center">
              <div className="bg-white p-2 rounded-xl border border-slate-200">
                <QRCodeCanvas value={typeof window !== 'undefined' ? window.location.href : ''} size={120} includeMargin />
              </div>
              <div className="mt-2 font-mono text-sm tracking-widest text-slate-500">{m.code}</div>
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-slate-400 mt-4">{t('publicLoyalty.footer')}</div>
      </div>
    </div>
  );
}
